/**
 * Animation configuration for Poker Reborn
 *
 * Timings based on legacy AS3 clients for proven feel.
 * See LEGACY_DATA_USAGE.md for source tracking.
 */

import type { EasingType } from '@/types';

// =============================================================================
// CARD ANIMATIONS
// =============================================================================

export const CARD_ANIMATIONS = {
  /** Deal hole cards to player */
  deal: {
    duration: 300,
    easing: 'easeOutQuad' as EasingType,
    startY: -400,        // Pixels above screen
    startAlpha: 0,
    /** Stagger delay between cards (ms) */
    staggerDelay: (playerCount: number) => 1000 / (playerCount * 2),
  },

  /** Flip card face up/down */
  flip: {
    duration: 250,
    easing: 'easeInOutQuad' as EasingType,
  },

  /** Fold cards (slide to muck) */
  fold: {
    duration: 200,
    easing: 'easeInQuad' as EasingType,
  },

  /** Highlight winning cards (move up) */
  highlight: {
    duration: 300,
    easing: 'easeOutQuad' as EasingType,
    yShift: -12,  // Pixels to move up
  },

  /** Community card reveal */
  community: {
    duration: 300,
    easing: 'easeOutQuad' as EasingType,
    /** Flop stagger delays (ms) */
    flopDelays: [0, 150, 300],
  },
};

// =============================================================================
// CHIP ANIMATIONS
// =============================================================================

export const CHIP_ANIMATIONS = {
  /** Bet chips fly to center */
  betToCenter: {
    duration: 400,
    easing: 'easeOutQuad' as EasingType,
  },

  /** Pot chips fly to winner */
  potToWinner: {
    duration: 500,
    easing: 'easeInOutQuad' as EasingType,
  },

  /** Chips stack up */
  stack: {
    duration: 200,
    easing: 'easeOutQuad' as EasingType,
    staggerDelay: 50,
  },

  /** Chips scatter (big win) */
  scatter: {
    duration: 600,
    easing: 'easeOutQuad' as EasingType,
  },
};

// =============================================================================
// DEALER BUTTON
// =============================================================================

export const DEALER_BUTTON_ANIMATION = {
  /** Move to next seat */
  move: {
    duration: 500,
    easing: 'easeInOutQuad' as EasingType,
  },
};

// =============================================================================
// TIMER
// =============================================================================

export const TIMER_ANIMATION = {
  /** Warning pulse (when time low) */
  warningPulse: {
    duration: 500,
    easing: 'easeInOutQuad' as EasingType,
    scale: 1.1,
  },

  /** Update interval (ms) */
  updateInterval: 100,
};

// =============================================================================
// UI TRANSITIONS
// =============================================================================

export const UI_ANIMATIONS = {
  /** Screen fade in/out */
  screenFade: {
    duration: 300,
    easing: 'easeInOutQuad' as EasingType,
  },

  /** Popup appear */
  popupIn: {
    duration: 250,
    easing: 'easeOutBack' as EasingType,
    startScale: 0.8,
  },

  /** Popup disappear */
  popupOut: {
    duration: 200,
    easing: 'easeInQuad' as EasingType,
  },

  /** Button press */
  buttonPress: {
    duration: 100,
    easing: 'easeOutQuad' as EasingType,
    scale: 0.95,
  },

  /** Highlight glow */
  glow: {
    duration: 600,
    easing: 'easeInOutQuad' as EasingType,
  },
};

// =============================================================================
// WIN EFFECTS
// =============================================================================

export const WIN_ANIMATIONS = {
  /** Winner highlight */
  highlight: {
    duration: 800,
    easing: 'easeInOutQuad' as EasingType,
    glowIntensity: 1.5,
  },

  /** Confetti burst */
  confetti: {
    duration: 2000,
    particleCount: 50,
  },

  /** Chip explosion (big win) */
  chipExplosion: {
    duration: 1500,
    particleCount: 30,
  },
};

// =============================================================================
// TIMING CONSTANTS
// =============================================================================

/** Delays between game events (ms) */
export const EVENT_DELAYS = {
  /** After deal, before first action */
  afterDeal: 500,

  /** After all players act, before next street */
  afterBettingRound: 300,

  /** After flop dealt */
  afterFlop: 400,

  /** After turn dealt */
  afterTurn: 300,

  /** After river dealt */
  afterRiver: 300,

  /** After showdown reveal (time for players to view results) */
  afterShowdown: 800,

  /** After pot awarded, before next hand */
  afterPotAwarded: 1500,

  /** Second action bell (from legacy) */
  secondBellDelay: 4000,
};

// =============================================================================
// GAME FLOW TIMING
// =============================================================================

/**
 * Game flow timing constants
 *
 * These control the pacing of game state transitions.
 * Effects run in parallel - they don't block these transitions.
 *
 * Note: In the Effects Engine architecture, game logic proceeds immediately
 * and these delays are used only for coordinating visual effects.
 */
export const GAME_FLOW_TIMING = {
  /**
   * Time to display showdown results before hand ends
   * Used by handleEnd() to show winner/cards before clearing table
   *
   * @deprecated Will be replaced by Effects Engine orchestration
   */
  showdownViewingTime: 3000,

  /**
   * Delay before starting next betting round after street change
   * Allows community cards animation to complete
   *
   * @deprecated Will be replaced by Effects Engine orchestration
   */
  streetAdvanceDelay: 1000,

  /**
   * Delay before mock opponent makes action (development only)
   */
  mockOpponentActionDelay: 800,

  /**
   * Default turn timer duration (server usually provides this)
   */
  defaultTurnTimeout: 30,

  /**
   * Delay before showing action panel when hero is first to act on flop/turn/river.
   * Allows street transition animations to complete before prompting action.
   */
  actionPanelFirstToActDelay: 2000,

  /**
   * Delay between sequential pot distributions
   * Main pot animates first, then side pots after this delay each.
   * Allows chip animation to complete before next pot starts.
   */
  potDistributionDelay: 800,
};

// =============================================================================
// STREET END ANIMATION TIMING
// =============================================================================

/**
 * Street End Animation Timing Constants
 *
 * When a betting round ends and a new street begins (flop/turn/river),
 * these constants control the 5-step sequential animation:
 *
 * Step 1: Bet chips → player pot area
 * Step 2: Wait for chips visibility
 * Step 3: Collect all player pots → table center
 * Step 4: Flip community cards
 * Step 5: Start action timer (handled by server 'act' message)
 *
 * @see docs/systems/STREET_END_ANIMATION.md
 */
export const STREET_END_TIMING = {
  /** Step 1: Chips fly from seat to player pot area */
  betToPlayerPot: 400,

  /** Step 2: Pause for chips visibility before collection */
  chipsVisibleWait: 600,

  /** Pause before chips fly to pot (between step 2 and 3) */
  preCollectPause: 300,

  /** Step 3: All player pots collect to table center */
  collectToPot: 400,

  /** Pause before cards deal (between step 3 and 4) */
  preCardsPause: 500,

  /** Step 3: Stagger delay between each player pot (ms) */
  collectStagger: 50,

  /** Step 4: Single card flip duration */
  cardFlip: 400,

  /** Step 4: Stagger between flop cards (flop only) */
  cardStagger: 150,

  /**
   * Maximum total duration (with bets + flop):
   * 400 + 600 + 400 + 700 = 2100ms (within 3s server buffer)
   */

  /**
   * Minimum total duration (check + turn/river):
   * 0 + 0 + 0 + 400 = 400ms
   */
};

// =============================================================================
// GAME END ANIMATION TIMING
// =============================================================================

/**
 * Game End Animation Timing Constants
 *
 * When a hand ends (fold or showdown), these constants control
 * the sequential animation:
 *
 * END-1: Collect final bets → table pot (+ moneyReturn simultaneously)
 * END-2: Reveal hole cards (showdown)
 * END-3: Highlight winning cards (per pot in loop)
 * END-4: Award pot → winner(s) (per pot in loop)
 *
 * @see docs/systems/animations/GAME_END_ANIMATION.md
 * @see src/effects/sequencer/GameEndSequencer.ts
 */
export const GAME_END_TIMING = {
  /** Initial delay at sequence start (can be 0) */
  initialDelay: 500,

  /** Delay to show final bet before collect (if last action != fold) */
  betToPotDelay: 1000,

  /** END-1: Collect final bets to pot duration */
  collectToPot: 400,

  /** END-1: Stagger delay between player pot collects */
  collectStagger: 200,

  /** Pause after END-1 collect completes (ensure chips visually settle) */
  postCollectPause: 300,

  /** END-2: Single card reveal duration */
  cardReveal: 300,

  /** END-2: Stagger between card reveals */
  cardRevealStagger: 150,

  /** Pause after END-2 cards revealed */
  cardsRevealedPause: 0,

  /** END-3: Winning cards highlight duration (per pot) */
  winningCardsHighlight: 500,

  /** Pause after END-3 highlight, before award */
  preAwardPause: 150,

  /** END-4: Pot award animation (per pot) */
  potAward: 600,

  /** Stagger between pots in loop */
  potAwardStagger: 300,

  /** Final pause to see results before next hand */
  outcomeDisplayPause: 2000,
};

// =============================================================================
// GAME START ANIMATION TIMING
// =============================================================================

/**
 * Game Start Animation Timing Constants
 *
 * When a new hand starts, these constants control the orchestrated sequence:
 *
 * START-1: Reset table (clear previous hand)
 * START-2: Animate blinds posting
 * START-3: Round-robin card dealing
 *
 * @see docs/systems/animations/GAME_START_ANIMATION.md
 * @see docs/systems/animations/GAME_START_SEQUENCER_PLAN.md
 * @see src/effects/sequencer/GameStartSequencer.ts
 */
export const GAME_START_TIMING = {
  /** Pause after START-1 reset, before blinds animation */
  postResetPause: 200,

  /** Pause after START-2 blinds posted, before dealing */
  postBlindsPause: 300,

  /** Pause after START-3 dealing complete, before action */
  postDealPause: 500,
};

// =============================================================================
// BLIND ANIMATION TIMING
// =============================================================================

/**
 * Blind Animation Timing Constants
 *
 * Controls the chip flying animation when blinds are posted (START-2).
 * Chips fly from SeatView to PlayerPotView.
 *
 * @see docs/systems/animations/CHIP_ANIMATION_SEAT_PLAYERPOT.md
 * @see docs/systems/animations/GAME_START_ANIMATION.md
 */
export const BLIND_ANIMATION_TIMING = {
  /** Duration of chip flight from seat to player pot (ms) */
  flightDuration: 400,

  /** Delay between SB and BB animation starts (ms) */
  blindStagger: 100,
};

// =============================================================================
// SOUND TIMING
// =============================================================================

/**
 * Sound Timing Constants
 *
 * Controls delays before sound effects play to sync with visual animations.
 * Adjust these values to fine-tune audio-visual synchronization.
 *
 * The chip sound should play when chips visually "appear" or "land",
 * not when the event fires (which may be before animation starts).
 */
export const SOUND_TIMING = {
  /** Delay before chip sound on bet action (ms) - sync with chip landing */
  chipBetDelay: 380,

  /** Delay before chip sound on call action (ms) - sync with chip landing */
  chipCallDelay: 380,

  /** Delay before chip sound on raise action (ms) - sync with chip landing */
  chipRaiseDelay: 380,

  /** Delay before chip sound on blinds posting (ms) */
  chipBlindsDelay: 380,

  /** Delay before chip sound on all-in (ms) - uses allin sound, not chip */
  allinSoundDelay: 0,
};

// =============================================================================
// EASING FUNCTIONS
// =============================================================================

/** Easing function implementations */
export const Easing: Record<EasingType, (t: number) => number> = {
  linear: (t) => t,

  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),

  easeInQuad: (t) => t * t,
  easeOutQuad: (t) => t * (2 - t),
  easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),

  easeInCubic: (t) => t * t * t,
  easeOutCubic: (t) => (--t) * t * t + 1,
  easeInOutCubic: (t) =>
    t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,

  easeInBack: (t) => {
    const s = 1.70158;
    return t * t * ((s + 1) * t - s);
  },
  easeOutBack: (t) => {
    const s = 1.70158;
    return --t * t * ((s + 1) * t + s) + 1;
  },
  easeInOutBack: (t) => {
    const s = 1.70158 * 1.525;
    if ((t *= 2) < 1) return 0.5 * (t * t * ((s + 1) * t - s));
    return 0.5 * ((t -= 2) * t * ((s + 1) * t + s) + 2);
  },

  easeInElastic: (t) => {
    if (t === 0 || t === 1) return t;
    return -Math.pow(2, 10 * (t - 1)) * Math.sin((t - 1.1) * 5 * Math.PI);
  },
  easeOutElastic: (t) => {
    if (t === 0 || t === 1) return t;
    return Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI) + 1;
  },

  easeInBounce: (t) => 1 - Easing.easeOutBounce(1 - t),
  easeOutBounce: (t) => {
    if (t < 1 / 2.75) {
      return 7.5625 * t * t;
    } else if (t < 2 / 2.75) {
      return 7.5625 * (t -= 1.5 / 2.75) * t + 0.75;
    } else if (t < 2.5 / 2.75) {
      return 7.5625 * (t -= 2.25 / 2.75) * t + 0.9375;
    } else {
      return 7.5625 * (t -= 2.625 / 2.75) * t + 0.984375;
    }
  },
};

/** Get easing function by name */
export function getEasing(name: EasingType): (t: number) => number {
  return Easing[name] || Easing.linear;
}
